import { isNil } from 'lodash'
import type { User } from '@/shared/models/userModel'
import type { Currency } from '@/shared/models/moneyModel'
import type { Company, PaymentDestination } from '@/shared/models/companyModel'
import type { LogicalFilter, LogicalOperator } from '@hello-aria/query-string'
import type { ComparisonFilterInput, NestedLogicalFilterInput } from '@hello-aria/query-string/dist/stringify'
import { EMPTY_VALUE } from '@hello-aria/ui/empty-value'

export const resolveBeneficiaryName = (user: Pick<User, 'businessName' | 'fullName' | 'email'> | undefined): string => {
    if (isNil(user)) return EMPTY_VALUE
    if (user.businessName) return user.businessName
    if (user.fullName) return user.fullName
    return user.email
}

export const getPaymentDestinationsByCurrency = (
    paymentDestinations: User['paymentDestinations'],
    currency: Currency
): PaymentDestination | undefined => {
    if (isNil(paymentDestinations) || paymentDestinations.length === 0) return undefined

    if (currency === 'EUR') {
        return paymentDestinations.find((paymentDestination) => paymentDestination.accountNumberType === 'IBAN')
    } else if (currency === 'GBP') {
        return paymentDestinations.find(
            (paymentDestination) =>
                paymentDestination.accountNumberType === 'ACCOUNT_NUMBER' ||
                paymentDestination.accountNumber.startsWith('GB')
        )
    } else {
        return undefined
    }
}

export const shouldDisplayOnboardingOrKyc = (user: User | undefined, company: Company | undefined): boolean => {
    if (!isNil(user?.onboarding) || (company?.config.needsKyc && !isNil(user?.kyc))) {
        return true
    }
    return false
}

export interface ResolveUsersFilters {
    type: LogicalFilter['type']
    operator: LogicalOperator
    filters: (
        | ComparisonFilterInput
        | {
              type: LogicalFilter['type']
              operator: LogicalOperator
              filters: ComparisonFilterInput[]
          }
    )[]
}

export type ResolveUsersFiltersParams = {
    customFilters?: (ComparisonFilterInput | NestedLogicalFilterInput)[]
}

export const resolveUserFilters = ({ customFilters }: ResolveUsersFiltersParams): ResolveUsersFilters => {
    const filters: (ComparisonFilterInput | NestedLogicalFilterInput)[] = customFilters ?? []

    return {
        type: 'logical',
        operator: 'and',
        filters
    }
}
